import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Card,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
} from "@mui/material";

import {
  QueryBuilderOutlinedIcon,
  AddTaskOutlinedIcon,
  CommentOutlinedIcon,
  AssignmentTurnedInOutlinedIcon,
} from "../access/icons/Icons";

import "../styles/users.scss";

import formatDate from "../components/FormatDate";
import { API_BASE_URL } from "../config";

const Dashboard = () => {
  const [summary, setSummary] = useState({});
  const [activity, setActivity] = useState([]);
  const [hours, setHours] = useState([]);
  // State for total hours/minutes from /api/admin/time-logs
  const [totalHours, setTotalHours] = useState(0);
  const [totalMinutes, setTotalMinutes] = useState(0);
  const [totalTasks, setTotalTasks] = useState(0);

  const [totalComments, setTotalComments] = useState(0);
  const [projectCount, setProjectCount] = useState(0);

  const [orderActivity, setOrderActivity] = useState("asc");
  const [orderByActivity, setOrderByActivity] = useState("name");

  const [orderHours, setOrderHours] = useState("asc");
  const [orderByHours, setOrderByHours] = useState("name");

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    const role = localStorage.getItem("role");
    const isVerified = localStorage.getItem("isVerified");

    if (!token || !role) {
      navigate("/login");
    } else if (isVerified !== "true") {
      navigate("/verify");
    }

    const headers = { Authorization: `Bearer ${token}` };

    fetch(`${API_BASE_URL}/api/user/dashboard-summary`, {
      headers,
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        const {
          totalHours,
          totalMinutes,
          totalComments,
          totalProjects,
          totalTasks,
          ...rest
        } = data || {};
        setSummary(rest);
        setTotalTasks(totalTasks || 0);
        setTotalComments(totalComments || 0);
        setProjectCount(totalProjects || 0);
      })
      .catch((err) => {
        console.error("Failed to fetch summary:", err);
        setSummary({});
      });

    // Fetch total hours and minutes for dashboard from /api/user/time-logs/total
    fetch(`${API_BASE_URL}/api/user/time-logs/total`, {
      headers,
      credentials: "include",
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load total time logs");
        return res.json();
      })
      .then((data) => {
        setTotalHours(data.totalHours || 0);
        setTotalMinutes(data.totalMinutes || 0);
      })
      .catch((err) => console.error("Failed to fetch total time logs:", err));

    // Activity
    fetch(`${API_BASE_URL}/api/user/recent-activity`, {
      headers,
      credentials: "include",
    })
      .then((res) => {
        if (!res.ok)
          throw new Error("Unauthorized or failed to load activity.");
        return res.json();
      })
      .then((data) => setActivity(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error("Failed to fetch activity:", err);
        setActivity([]);
      });

    // Last Logged Hours (for table)
    fetch(`${API_BASE_URL}/api/user/last-logged-hours`, {
      headers,
      credentials: "include",
    })
      .then((res) => {
        if (!res.ok) throw new Error("Unauthorized or failed to load hours.");
        return res.json();
      })
      .then((data) => {
        setHours(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error("Failed to fetch time logs:", err);
        setHours([]);
      });
  }, []);

  const handleSortActivity = (property) => {
    const isAsc = orderByActivity === property && orderActivity === "asc";
    setOrderActivity(isAsc ? "desc" : "asc");
    setOrderByActivity(property);
  };

  const sortedActivity = [...activity].sort((a, b) => {
    if (a[orderByActivity] < b[orderByActivity])
      return orderActivity === "asc" ? -1 : 1;
    if (a[orderByActivity] > b[orderByActivity])
      return orderActivity === "asc" ? 1 : -1;
    return 0;
  });

  const handleSortHours = (property) => {
    const isAsc = orderByHours === property && orderHours === "asc";
    setOrderHours(isAsc ? "desc" : "asc");
    setOrderByHours(property);
  };

  const sortedHours = [...hours].sort((a, b) => {
    if (a[orderByHours] < b[orderByHours]) return orderHours === "asc" ? -1 : 1;
    if (a[orderByHours] > b[orderByHours]) return orderHours === "asc" ? 1 : -1;
    return 0;
  });

  const statCards = [
    {
      title: "Total Projects",
      value: projectCount || 0,
      icon: (
        <AssignmentTurnedInOutlinedIcon
          sx={{ color: "#4caf50", fontSize: 50 }}
        />
      ),
      view: "View my projects",
      link: "/user/projects",
    },
    {
      title: "Total Hours",
      value: `${totalHours}h ${totalMinutes}min`,
      icon: (
        <QueryBuilderOutlinedIcon sx={{ color: "#9c27b0", fontSize: 50 }} />
      ),
      view: "View my hours",
      link: "/user/my-time",
    },
    {
      title: "New Comments",
      value: Number(totalComments) || 0,
      icon: <CommentOutlinedIcon sx={{ color: "#3f51b5", fontSize: 50 }} />,
      view: "View my comments",
      link: "/user/comments",
    },
    {
      title: "Total Tasks",
      value: totalTasks || 0,
      icon: <AddTaskOutlinedIcon sx={{ color: "#3f51b5", fontSize: 50 }} />,
      view: "View my tasks",
      link: "/user/tasks",
    },
  ];

  return (
    <>
      <Box className="admin-deshboard">
        <Typography variant="h5" fontWeight={500} mb={3}>
          Dashboard
        </Typography>

        <Box
          className="user-box"
          display="grid"
          gridTemplateColumns="repeat(auto-fit, minmax(260px, 1fr))"
          gap={3}
          mb={4}
        >
          {statCards.map((card, i) => (
            <Card
              key={i}
              elevation={3}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                p: 3,
                borderRadius: 3,
                minHeight: "180px",
                textAlign: "left",
                boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
              }}
            >
              <Typography color="#000" fontSize={18} fontWeight={600} mb={1}>
                {card.title}
              </Typography>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="h4" fontWeight={700}>
                  {card.value}
                </Typography>
                <Box fontSize="34px">{card.icon}</Box>
              </Box>
              <Typography color="primary" sx={{ mt: 1 }}>
                <a
                  style={{
                    color: "#0b41cd",
                    fontWeight: 600,
                    textDecoration: "none",
                  }}
                  href={card.link}
                >
                  {card.view}
                </a>
              </Typography>
            </Card>
          ))}
        </Box>

        {/* Recent Activity Table */}
        <Typography variant="h6" mb={1} mt={5}>
          Recent Activity
        </Typography>
        <Box sx={{ overflowX: "auto", width: "100%" }}>
          <Table className="table">
            <TableHead>
              <TableRow>
                <TableCell
                  sortDirection={
                    orderByActivity === "name" ? orderActivity : false
                  }
                >
                  <TableSortLabel
                    active={orderByActivity === "name"}
                    direction={
                      orderByActivity === "name" ? orderActivity : "asc"
                    }
                    onClick={() => handleSortActivity("name")}
                  >
                    User
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    orderByActivity === "activity_type" ? orderActivity : false
                  }
                >
                  <TableSortLabel
                    active={orderByActivity === "activity_type"}
                    direction={
                      orderByActivity === "activity_type"
                        ? orderActivity
                        : "asc"
                    }
                    onClick={() => handleSortActivity("activity_type")}
                  >
                    Activity
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    orderByActivity === "project" ? orderActivity : false
                  }
                >
                  <TableSortLabel
                    active={orderByActivity === "project"}
                    direction={
                      orderByActivity === "project" ? orderActivity : "asc"
                    }
                    onClick={() => handleSortActivity("project")}
                  >
                    Project
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    orderByActivity === "time" ? orderActivity : false
                  }
                >
                  <TableSortLabel
                    active={orderByActivity === "time"}
                    direction={
                      orderByActivity === "time" ? orderActivity : "asc"
                    }
                    onClick={() => handleSortActivity("time")}
                  >
                    Time
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedActivity.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No activity.
                  </TableCell>
                </TableRow>
              ) : (
                sortedActivity.map((a, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <div>{a.name}</div>
                      <Typography variant="caption" color="text.secondary">
                        {a.email}
                      </Typography>
                    </TableCell>
                    <TableCell>{a.activity_type}</TableCell>
                    <TableCell>
                      {a.project_title || a.project || "N/A"}
                    </TableCell>
                    <TableCell>{formatDate(a.timestamp)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>

        {/* Last Logged Hours Table */}
        <Typography variant="h6" mb={1} mt={5}>
          Last Logged Hours
        </Typography>

        <Box sx={{ overflowX: "auto", width: "100%" }}>
          <Table className="table">
            <TableHead>
              <TableRow>
                <TableCell
                  sortDirection={orderByHours === "name" ? orderHours : false}
                >
                  <TableSortLabel
                    active={orderByHours === "name"}
                    direction={orderByHours === "name" ? orderHours : "asc"}
                    onClick={() => handleSortHours("name")}
                  >
                    User
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    orderByHours === "project" ? orderHours : false
                  }
                >
                  <TableSortLabel
                    active={orderByHours === "project"}
                    direction={orderByHours === "project" ? orderHours : "asc"}
                    onClick={() => handleSortHours("project")}
                  >
                    Project
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    orderByHours === "start_time" ? orderHours : false
                  }
                >
                  <TableSortLabel
                    active={orderByHours === "start_time"}
                    direction={
                      orderByHours === "start_time" ? orderHours : "asc"
                    }
                    onClick={() => handleSortHours("start_time")}
                  >
                    Start
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    orderByHours === "end_time" ? orderHours : false
                  }
                >
                  <TableSortLabel
                    active={orderByHours === "end_time"}
                    direction={orderByHours === "end_time" ? orderHours : "asc"}
                    onClick={() => handleSortHours("end_time")}
                  >
                    End
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    orderByHours === "duration" ? orderHours : false
                  }
                >
                  <TableSortLabel
                    active={orderByHours === "duration"}
                    direction={orderByHours === "duration" ? orderHours : "asc"}
                    onClick={() => handleSortHours("duration")}
                  >
                    Duration
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedHours.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No logs.
                  </TableCell>
                </TableRow>
              ) : (
                sortedHours.map((h, i) => (
                  <TableRow key={i}>
                    <TableCell>{h.name}</TableCell>
                    <TableCell>
                      {h.project_title || h.project || "N/A"}
                    </TableCell>
                    <TableCell>{h.start_time}</TableCell>
                    <TableCell>{h.end_time}</TableCell>
                    <TableCell>
                      {h.duration !== null
                        ? `${h.duration} min`
                        : "In progress"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </>
  );
};

export default Dashboard;
